<script setup lang="ts">
/**
 * One comment: avatar, author, „vor 5 Minuten“ (with „bearbeitet“), the text
 * with light markdown and @-mentions (`renderMentionText`, no `v-html`), and —
 * for the viewer's own comment — edit in place and delete. Delete asks first
 * through `useNxConfirm` unless `confirmDelete` is off. With `resolvable` a
 * toggle marks it „Erledigt“; a resolved comment is shown muted.
 *
 * The kit emits, the caller saves: `edit(id, draft)`, `delete(id)`,
 * `resolve(id, resolved)`. `#author` replaces the name (e.g. an
 * `NxPersonChip`), `#actions` adds buttons (reply, react).
 */
import { computed, nextTick, ref } from 'vue';
import Check from 'lucide-vue-next/dist/esm/icons/check.js'; import Pencil from 'lucide-vue-next/dist/esm/icons/pencil.js'; import RotateCcw from 'lucide-vue-next/dist/esm/icons/rotate-ccw.js'; import Trash2 from 'lucide-vue-next/dist/esm/icons/trash-2.js';
import NxAvatar from '../Avatar/NxAvatar.vue';
import NxButton from '../Button/NxButton.vue';
import NxIconButton from '../IconButton/NxIconButton.vue';
import { useNxConfirm } from '../Confirm/useNxConfirm';
import type { NxMention, NxMentionCandidate } from '../Mentions/types';
import { renderMentionText } from '../Mentions/render';
import NxRelativeTime from '../RelativeTime/NxRelativeTime.vue';
import NxMentionTextarea from './NxMentionTextarea.vue';
import { NX_COMMENT_LABELS, type NxCommentLabels } from './labels';
import type { NxComment, NxCommentDraft } from './types';

const props = withDefaults(
  defineProps<{
    comment: NxComment;
    /** People for @-mentions while editing. */
    candidates?: readonly NxMentionCandidate[];
    /** Show edit; default: `comment.own`. */
    editable?: boolean;
    /** Show delete; default: `comment.own`. */
    deletable?: boolean;
    /** Show the resolve toggle. */
    resolvable?: boolean;
    /** Ask before deleting. */
    confirmDelete?: boolean;
    /** A save is running: buttons wait. */
    busy?: boolean;
    /** Mentions become buttons that emit `mention`. */
    mentionsInteractive?: boolean;
    /** Logical px of the avatar. */
    avatarSize?: number;
    labels?: Partial<NxCommentLabels>;
    locale?: string;
  }>(),
  {
    candidates: () => [],
    editable: undefined,
    deletable: undefined,
    resolvable: false,
    confirmDelete: true,
    busy: false,
    mentionsInteractive: false,
    avatarSize: 32,
    labels: undefined,
    locale: undefined,
  }
);

const emit = defineEmits<{
  edit: [id: NxComment['id'], draft: NxCommentDraft];
  delete: [id: NxComment['id']];
  resolve: [id: NxComment['id'], resolved: boolean];
  mention: [mention: NxMention];
}>();

defineSlots<{
  avatar?(): unknown;
  author?(): unknown;
  /** Extra buttons in the header, before edit and delete. */
  actions?(): unknown;
}>();

const t = computed<NxCommentLabels>(() => ({ ...NX_COMMENT_LABELS, ...props.labels }));
const confirm = useNxConfirm();

const root = ref<HTMLElement | null>(null);
const editor = ref<InstanceType<typeof NxMentionTextarea> | null>(null);

const canEdit = computed(() => props.editable ?? !!props.comment.own);
const canDelete = computed(() => props.deletable ?? !!props.comment.own);
const segments = computed(() =>
  renderMentionText(props.comment.body, [...(props.comment.mentions ?? [])])
);

const editing = ref(false);
const draftBody = ref('');
const draftMentions = ref<NxMention[]>([]);
/** Who is mentioned where — a mention swapped for another person counts as a change. */
const mentionKey = (mentions: readonly NxMention[] | undefined) =>
  (mentions ?? [])
    .map((m) => `${m.accountId}@${m.start}-${m.end}`)
    .sort()
    .join(',');
const canSave = computed(
  () =>
    !props.busy &&
    !!draftBody.value.trim() &&
    (draftBody.value !== props.comment.body ||
      mentionKey(draftMentions.value) !== mentionKey(props.comment.mentions))
);

function startEdit() {
  draftBody.value = props.comment.body;
  draftMentions.value = (props.comment.mentions ?? []).map((m) => ({ ...m }));
  editing.value = true;
  void nextTick(() => editor.value?.focus());
}

function stopEdit() {
  editing.value = false;
  void nextTick(() => root.value?.focus({ preventScroll: true }));
}

function save() {
  if (!canSave.value) return;
  // Mentions index into the untrimmed text: re-base them like the chat composer.
  const raw = draftBody.value;
  const body = raw.trim();
  const lead = raw.length - raw.trimStart().length;
  const mentions = draftMentions.value
    .map((m) => ({ ...m, start: m.start - lead, end: m.end - lead }))
    .filter((m) => m.start >= 0 && m.end <= body.length);
  emit('edit', props.comment.id, { body, mentions });
  stopEdit();
}

async function remove() {
  if (props.confirmDelete) {
    const ok = await confirm({
      title: t.value.deleteTitle,
      message: t.value.deleteMessage,
      confirmLabel: t.value.delete,
      tone: 'danger',
    });
    if (!ok) return;
  }
  emit('delete', props.comment.id);
}
</script>

<template>
  <article
    ref="root"
    class="nx-comment-item"
    :class="{ 'is-own': comment.own, 'is-resolved': comment.resolved, 'is-editing': editing }"
    tabindex="-1"
    :data-comment-id="comment.id"
    data-testid="nx-comment-item"
  >
    <div class="nx-comment-item__avatar">
      <slot name="avatar">
        <NxAvatar
          :name="comment.author.name"
          :src="comment.author.src"
          :initials="comment.author.initials"
          :size="avatarSize"
          decorative
        />
      </slot>
    </div>
    <div class="nx-comment-item__main">
      <header class="nx-comment-item__head">
        <span class="nx-comment-item__author">
          <slot name="author">{{ comment.author.name }}</slot>
        </span>
        <NxRelativeTime
          class="nx-comment-item__time"
          :value="comment.createdAt"
          format="short"
          :locale="locale"
        />
        <span v-if="comment.edited" class="nx-comment-item__edited">{{ t.edited }}</span>
        <span v-if="comment.resolved" class="nx-comment-item__badge">
          <Check class="nxt-icon-xs" aria-hidden="true" />
          {{ t.resolved }}
        </span>
        <span v-if="!editing" class="nx-comment-item__actions">
          <slot name="actions" />
          <NxIconButton
            v-if="resolvable"
            height="s"
            :disabled="busy"
            :title="comment.resolved ? t.reopen : t.resolve"
            data-testid="nx-comment-resolve"
            @click="emit('resolve', comment.id, !comment.resolved)"
          >
            <RotateCcw v-if="comment.resolved" class="nxt-icon-sm" />
            <Check v-else class="nxt-icon-sm" />
          </NxIconButton>
          <NxIconButton
            v-if="canEdit"
            height="s"
            :disabled="busy"
            :title="t.edit"
            data-testid="nx-comment-edit"
            @click="startEdit"
          >
            <Pencil class="nxt-icon-sm" />
          </NxIconButton>
          <NxIconButton
            v-if="canDelete"
            height="s"
            danger
            :disabled="busy"
            :title="t.delete"
            data-testid="nx-comment-delete"
            @click="remove"
          >
            <Trash2 class="nxt-icon-sm" />
          </NxIconButton>
        </span>
      </header>

      <div v-if="!editing" class="nx-comment-item__body">
        <template v-for="(seg, index) in segments" :key="index">
          <template v-if="seg.kind === 'text'">{{ seg.value }}</template>
          <strong v-else-if="seg.kind === 'bold'">{{ seg.value }}</strong>
          <em v-else-if="seg.kind === 'italic'">{{ seg.value }}</em>
          <code v-else-if="seg.kind === 'code'">{{ seg.value }}</code>
          <br v-else-if="seg.kind === 'break'" />
          <button
            v-else-if="seg.kind === 'mention' && mentionsInteractive"
            type="button"
            class="nx-comment-item__mention"
            :title="seg.mention.name"
            @click="emit('mention', seg.mention)"
          >
            {{ seg.mention.label }}
          </button>
          <span
            v-else-if="seg.kind === 'mention'"
            class="nx-comment-item__mention"
            :title="seg.mention.name"
          >
            {{ seg.mention.label }}
          </span>
        </template>
      </div>

      <div v-else class="nx-comment-item__edit">
        <NxMentionTextarea
          ref="editor"
          v-model="draftBody"
          v-model:mentions="draftMentions"
          :candidates="candidates"
          :disabled="busy"
          :label="t.edit"
          @submit="save"
          @cancel="stopEdit"
        />
        <div class="nx-comment-item__edit-bar">
          <span class="nx-comment-item__hint">{{ t.editHint }}</span>
          <NxButton variant="ghost" :disabled="busy" @click="stopEdit">{{ t.cancel }}</NxButton>
          <NxButton :disabled="!canSave" data-testid="nx-comment-save" @click="save">
            {{ t.save }}
          </NxButton>
        </div>
      </div>
    </div>
  </article>
</template>

<style scoped>
.nx-comment-item {
  display: flex;
  gap: var(--nxt-space-sm);
  min-width: 0;
  padding: var(--nxt-space-xs);
  border-radius: var(--nxt-radius-lg);
  outline: none;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-comment-item:hover,
.nx-comment-item:focus-within {
  background: color-mix(in srgb, var(--nxt-surface-sunken-bg) 70%, transparent);
}
.nx-comment-item:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-comment-item__avatar {
  flex: none;
  padding-block-start: calc(var(--nxt-space-2xs) / 2);
}
.nx-comment-item__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

.nx-comment-item__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: var(--nxt-space-xs);
  row-gap: var(--nxt-space-2xs);
  min-height: var(--nxt-control-size-sm);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
.nx-comment-item__author {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
}
.nx-comment-item__edited {
  font-style: italic;
}
.nx-comment-item__edited::before {
  content: '·';
  margin-inline-end: var(--nxt-space-xs);
  font-style: normal;
}
.nx-comment-item__badge {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: calc(var(--nxt-space-2xs) / 2) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-success-subtle-bg);
  color: var(--nxt-success-subtle-fg);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-comment-item__actions {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  margin-inline-start: auto;
  opacity: 0;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-comment-item:hover .nx-comment-item__actions,
.nx-comment-item:focus-within .nx-comment-item__actions {
  opacity: 1;
}
@media (hover: none) {
  .nx-comment-item__actions {
    opacity: 1;
  }
}

.nx-comment-item__body {
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg);
  overflow-wrap: anywhere;
}
.nx-comment-item.is-resolved .nx-comment-item__body {
  color: var(--nxt-fg-muted);
}
.nx-comment-item__body code {
  padding: 0 var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-sunken-bg);
  font-family: var(--nxt-font-mono);
  font-size: 0.9em;
}
.nx-comment-item__mention {
  display: inline;
  margin: 0;
  padding: 0 calc(var(--nxt-space-2xs) * 0.75);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
  font: inherit;
  font-weight: var(--nxt-font-weight-semibold);
}
button.nx-comment-item__mention {
  cursor: pointer;
}
button.nx-comment-item__mention:hover {
  background: var(--nxt-brand-subtle-bg-hover);
}
button.nx-comment-item__mention:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

.nx-comment-item__edit {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
}
.nx-comment-item__edit-bar {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}
.nx-comment-item__hint {
  flex: 1;
  min-width: 0;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
@media (prefers-reduced-motion: reduce) {
  .nx-comment-item,
  .nx-comment-item__actions {
    transition: none;
  }
}
</style>
